const initEliteHeader = () => {
  const header = document.querySelector("[data-elite-header]");

  if (!header) {
    return;
  }

  const mobileMenu = header.querySelector("[data-mobile-menu]");
  const mobileToggle = header.querySelector("[data-mobile-menu-toggle]");
  const mobileCloseButtons = Array.from(header.querySelectorAll("[data-mobile-menu-close]"));
  const mobileNav = header.querySelector(".elite-mobile-menu__nav");
  const desktopNav = header.querySelector(".elite-header__nav");
  const desktopMenus = Array.from(desktopNav?.querySelectorAll(".elite-header__nav-item") || [])
    .map((item) => ({
      item,
      toggle: item.querySelector(":scope > .elite-header__nav-link[aria-controls]"),
      panel: item.querySelector(":scope > .elite-header__mega")
    }))
    .filter((menu) => menu.toggle && menu.panel);
  const searchDialog = header.querySelector("[data-search-dialog]");
  const searchPanel = searchDialog?.querySelector("[data-search-panel]");
  const searchInput = searchDialog?.querySelector("[data-search-input]");
  const searchTriggers = Array.from(header.querySelectorAll("[data-search-open]"));
  const searchCloseButtons = Array.from(header.querySelectorAll("[data-search-close]"));
  let openDesktopMenu = null;
  let ignoreNextMobileClick = false;
  let scrollDismissFrame = 0;
  let searchReturnFocus = null;
  let mobileSubmenuTrigger = null;

  const normalizeConfigureLinks = () => {
    header.querySelectorAll('a[href*="/collections/gaming-pcs"]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute("href"), window.location.origin);
        const isGamingPcsCollection = url.pathname.replace(/\/$/, "") === "/collections/gaming-pcs";

        if (!isGamingPcsCollection) return;

        url.searchParams.set("page", "1");
        link.setAttribute("href", `${url.pathname}${url.search}`);
      } catch {
        // Leave unusual URLs untouched.
      }
    });
  };

  const isMobileMenuOpen = () => Boolean(
    mobileMenu
    && !mobileMenu.hidden
    && mobileMenu.getAttribute("aria-hidden") !== "true"
  );

  const setMobileMenu = (isOpen) => {
    if (!mobileMenu || !mobileToggle) return;
    const wasOpen = isMobileMenuOpen();

    if (isOpen) {
      closeDesktopMenus();
      mobileMenu.hidden = false;
      mobileMenu.removeAttribute("hidden");
    } else {
      mobileMenu.hidden = true;
      mobileMenu.setAttribute("hidden", "");
    }

    mobileMenu.classList.toggle("is-open", isOpen);
    mobileMenu.toggleAttribute("data-menu-open", isOpen);
    mobileMenu.setAttribute("aria-hidden", String(!isOpen));
    mobileToggle.setAttribute("aria-expanded", String(isOpen));
    mobileToggle.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
    mobileCloseButtons.forEach((button) => {
      button.setAttribute("aria-expanded", String(isOpen));
    });
    document.documentElement.classList.toggle("mobile-menu-open", isOpen);
    document.body?.classList.toggle("mobile-menu-open", isOpen);

    if (isOpen) {
      window.requestAnimationFrame(() => mobileCloseButtons[0]?.focus({ preventScroll: true }));
    } else {
      closeMobileSubmenu();
      if (wasOpen && mobileMenu.contains(document.activeElement)) mobileToggle.focus({ preventScroll: true });
    }
  };

  const isSearchOpen = () => Boolean(
    searchDialog
    && !searchDialog.hidden
    && searchDialog.getAttribute("aria-hidden") !== "true"
  );

  const setSearchDialog = (isOpen, returnFocus = true) => {
    if (!searchDialog) return;

    if (isOpen) {
      searchReturnFocus = mobileMenu?.contains(document.activeElement)
        ? mobileToggle
        : (document.activeElement instanceof HTMLElement ? document.activeElement : searchTriggers[0]);
      setMobileMenu(false);
      closeDesktopMenus();
      searchDialog.hidden = false;
      searchDialog.removeAttribute("hidden");
    } else {
      searchDialog.hidden = true;
      searchDialog.setAttribute("hidden", "");
    }

    searchDialog.setAttribute("aria-hidden", String(!isOpen));
    searchTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", String(isOpen));
    });
    document.documentElement.classList.toggle("search-dialog-open", isOpen);
    document.body?.classList.toggle("search-dialog-open", isOpen);

    if (isOpen) {
      window.requestAnimationFrame(() => {
        searchInput?.focus({ preventScroll: true });
        searchInput?.select();
      });
    } else if (returnFocus && searchReturnFocus?.isConnected) {
      searchReturnFocus.focus({ preventScroll: true });
    }
  };

  const trapSearchFocus = (event) => {
    if (event.key !== "Tab" || !isSearchOpen() || !searchPanel) return;

    const focusable = Array.from(searchPanel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const trapMobileFocus = (event) => {
    if (event.key !== "Tab" || !isMobileMenuOpen()) return;
    const items = Array.from(mobileMenu.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), summary, [tabindex="0"]'))
      .filter(element => element.getClientRects().length > 0);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first) return;
    if (event.shiftKey && (document.activeElement === first || !mobileMenu.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !mobileMenu.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  };

  const getMobileMenuLabel = (menuItem) => {
    const label = menuItem.querySelector(".elite-mobile-menu__summary span, .elite-mobile-menu__link span");
    return (label?.childNodes?.[0]?.textContent || label?.textContent || "Menu").trim();
  };

  const getMobileMenuUrl = (menuItem, trigger) => {
    const triggerHref = trigger?.matches?.("a[href]") ? trigger.getAttribute("href") : "";
    const menuUrl = menuItem.dataset.mobileMenuUrl || "";
    const viewAllLink = Array.from(menuItem.querySelectorAll(".elite-mobile-menu__submenu a[href]"))
      .find((link) => link.textContent.trim().toLowerCase().startsWith("view all"));

    return triggerHref || menuUrl || viewAllLink?.getAttribute("href") || "";
  };

  const closeMobileSubmenu = () => {
    if (!mobileNav) return;

    mobileNav.classList.remove("is-submenu-active");
    mobileNav.querySelectorAll(".elite-mobile-menu__details.is-submenu-current").forEach((item) => {
      item.classList.remove("is-submenu-current");
      item.removeAttribute("open");
      item.querySelector(".elite-mobile-menu__submenu-header")?.remove();
    });
    if (mobileSubmenuTrigger?.isConnected && isMobileMenuOpen()) mobileSubmenuTrigger.focus({ preventScroll: true });
    mobileSubmenuTrigger = null;
  };

  const openMobileSubmenu = (menuItem, trigger) => {
    if (!mobileNav || !menuItem) return;

    closeMobileSubmenu();
    const submenu = menuItem.querySelector(".elite-mobile-menu__submenu");
    if (!submenu) return;

    mobileSubmenuTrigger = trigger;
    const label = getMobileMenuLabel(menuItem);
    const menuUrl = getMobileMenuUrl(menuItem, trigger);
    const eyebrow = menuItem.dataset.mobileMenuEyebrow || "";
    const displayTitle = menuItem.dataset.mobileMenuTitle || label;
    const headerRow = document.createElement("div");
    headerRow.className = "elite-mobile-menu__submenu-header";

    const backButton = document.createElement("button");
    backButton.className = "elite-mobile-menu__submenu-back";
    backButton.type = "button";
    backButton.setAttribute("aria-label", "Back to main menu");
    backButton.innerHTML = '<span aria-hidden="true">‹</span>';
    backButton.addEventListener("click", closeMobileSubmenu);

    const titleGroup = document.createElement("div");
    titleGroup.className = "elite-mobile-menu__submenu-heading";

    if (eyebrow) {
      const eyebrowText = document.createElement("small");
      eyebrowText.className = "elite-mobile-menu__submenu-eyebrow";
      eyebrowText.textContent = eyebrow;
      titleGroup.append(eyebrowText);
    }

    if (menuUrl) {
      const titleLink = document.createElement("a");
      titleLink.className = "elite-mobile-menu__submenu-title";
      titleLink.href = menuUrl;
      titleLink.textContent = displayTitle;
      titleGroup.append(titleLink);
    } else {
      const title = document.createElement("strong");
      title.className = "elite-mobile-menu__submenu-title";
      title.textContent = displayTitle;
      titleGroup.append(title);
    }

    headerRow.append(backButton, titleGroup);

    submenu.prepend(headerRow);

    menuItem.setAttribute("open", "");
    menuItem.classList.add("is-submenu-current");
    mobileNav.classList.add("is-submenu-active");
    warmMenuImages(submenu, true);
    backButton.focus({ preventScroll: true });
  };

  const closeDesktopMenus = (returnFocus = false) => {
    const previousMenu = openDesktopMenu;
    desktopMenus.forEach(({ item, toggle, panel }) => {
      item.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      panel.hidden = true;
    });
    openDesktopMenu = null;
    if (returnFocus) previousMenu?.toggle.focus({ preventScroll: true });
  };

  const setDesktopMenu = (menu, isOpen) => {
    closeDesktopMenus();
    if (!isOpen || !desktopNav?.getClientRects().length) return;

    if (isSearchOpen()) setSearchDialog(false, false);
    setMobileMenu(false);
    menu.panel.hidden = false;
    menu.item.classList.add("is-open");
    menu.toggle.setAttribute("aria-expanded", "true");
    openDesktopMenu = menu;
    warmMenuImages(menu.panel, true);
  };

  function warmMenuImages(scope, visible = false) {
    if (!scope) return;
    scope.querySelectorAll('img').forEach(image => {
      if (visible) image.fetchPriority = 'auto';
      if (!image.src || image.dataset.navImageWarmed === 'true') return;
      image.dataset.navImageWarmed = 'true';
      if (!visible) image.fetchPriority = 'low';
      // Let the real responsive image resolve its own srcset. Creating a
      // separate Image used the fallback size and fetched closed menus too.
      image.setAttribute('loading', 'eager');
    });
  }
  for (const {toggle,panel} of desktopMenus) {
    for (const event of ['pointerenter','focus','touchstart']) {
      toggle.addEventListener(event, () => warmMenuImages(panel), {passive:true});
    }
  }

  const queueScrollMenuDismissal = () => {
    if (scrollDismissFrame) return;

    scrollDismissFrame = window.requestAnimationFrame(() => {
      scrollDismissFrame = 0;
      closeDesktopMenus();
    });
  };

  const isPlainLeftClick = (event) => (
    !event.defaultPrevented
    && event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey
  );

  const gameDetailUrl = (link) => {
    try {
      const url = new URL(link.href, window.location.origin);
      const pathMatch = url.pathname.match(/\/collections\/pcs-by-game\/([^/]+?)(?:-gaming-pc)?\/?$/i);

      if (url.pathname.replace(/\/$/, "") === "/collections/pcs-by-game" && url.searchParams.get("game")) {
        return url.href;
      }

      if (!pathMatch || !pathMatch[1]) return "";

      return `${url.origin}/collections/pcs-by-game?game=${encodeURIComponent(pathMatch[1])}`;
    } catch {
      return "";
    }
  };

  header.addEventListener("click", (event) => {
    const link = event.target.closest(
      ".elite-header__mega--games .elite-header__mega-game-card[href], [data-live-games-mobile] .elite-mobile-menu__submenu-link--image[href]"
    );
    const targetUrl = link ? gameDetailUrl(link) : "";

    if (!link || !targetUrl || !isPlainLeftClick(event)) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(targetUrl);
  }, true);

  mobileToggle?.addEventListener("click", (event) => {
    event.preventDefault();
    if (ignoreNextMobileClick) {
      ignoreNextMobileClick = false;
      return;
    }

    setMobileMenu(!isMobileMenuOpen());
  });

  mobileToggle?.addEventListener("pointerup", (event) => {
    if (event.pointerType === "mouse") return;

    event.preventDefault();
    ignoreNextMobileClick = true;
    window.setTimeout(() => {
      ignoreNextMobileClick = false;
    }, 250);
    setMobileMenu(!isMobileMenuOpen());
  });

  mobileCloseButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (ignoreNextMobileClick) {
        ignoreNextMobileClick = false;
        return;
      }

      setMobileMenu(false);
    });
  });

  searchTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      if (!isPlainLeftClick(event)) return;

      event.preventDefault();
      setSearchDialog(true);
    });
  });

  searchCloseButtons.forEach((button) => {
    button.addEventListener("click", () => setSearchDialog(false));
  });

  mobileNav?.addEventListener("click", (event) => {
    const shortcut = event.target.closest(".epc-mobile-shortcuts a[href]");
    if (shortcut && isPlainLeftClick(event)) {
      event.preventDefault();
      const destination = shortcut.href;
      setMobileMenu(false);
      window.location.assign(destination);
      return;
    }

    const trigger = event.target.closest(".elite-mobile-menu__summary, .elite-mobile-menu__summary--link");
    const menuItem = trigger?.closest(".elite-mobile-menu__details");

    if (!trigger || !menuItem?.querySelector(".elite-mobile-menu__submenu")) return;

    event.preventDefault();
    openMobileSubmenu(menuItem, trigger);
  });

  closeDesktopMenus();
  desktopMenus.forEach((menu) => {
    menu.toggle.addEventListener("click", (event) => {
      // Keep native middle-click, modified clicks and the link context menu.
      if (!isPlainLeftClick(event)) return;

      event.preventDefault();
      setDesktopMenu(menu, openDesktopMenu !== menu);
    });
    menu.toggle.addEventListener("keydown", (event) => {
      if (event.key === " " && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        setDesktopMenu(menu, openDesktopMenu !== menu);
        return;
      }

      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

      event.preventDefault();
      setDesktopMenu(menu, true);
      const links = Array.from(menu.panel.querySelectorAll("a[href], button:not([disabled])"))
        .filter((link) => link.getClientRects().length > 0);
      const target = event.key === "ArrowUp" ? links[links.length - 1] : links[0];
      target?.focus({ preventScroll: true });
    });
  });
  desktopNav?.addEventListener("focusout", (event) => {
    if (openDesktopMenu && !openDesktopMenu.item.contains(event.relatedTarget)) closeDesktopMenus();
  });

  normalizeConfigureLinks();

  new MutationObserver(() => {
    if (openDesktopMenu) warmMenuImages(openDesktopMenu.panel, true);
    mobileNav?.querySelectorAll('.is-submenu-current .elite-mobile-menu__submenu').forEach(panel => warmMenuImages(panel, true));
    normalizeConfigureLinks();
  }).observe(header, {
    childList: true,
    subtree: true
  });

  document.addEventListener("click", (event) => {
    if (openDesktopMenu && !openDesktopMenu.item.contains(event.target)) closeDesktopMenus();
  });

  document.addEventListener("keydown", (event) => {
    trapSearchFocus(event);
    trapMobileFocus(event);

    if (event.key !== "Escape") return;

    if (isSearchOpen()) {
      event.preventDefault();
      setSearchDialog(false);
      return;
    }

    if (mobileNav?.classList.contains("is-submenu-active")) {
      closeMobileSubmenu();
      return;
    }

    setMobileMenu(false);
    if (openDesktopMenu) {
      event.preventDefault();
      closeDesktopMenus(true);
    }
  });

  window.addEventListener("scroll", queueScrollMenuDismissal, { passive: true });
  window.addEventListener("resize", () => {
    if (window.matchMedia("(min-width: 1121px)").matches && isMobileMenuOpen()) setMobileMenu(false);
    closeDesktopMenus();
  }, { passive: true });
  const resetArrivalMenus = () => {
    setMobileMenu(false);
    closeDesktopMenus();
  };
  // pagehide also saves a closed header in the back/forward cache.
  window.addEventListener("pagehide", resetArrivalMenus);
  window.addEventListener("pageshow", resetArrivalMenus);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initEliteHeader);
} else {
  initEliteHeader();
}

const initElitePrivacyBannerLayer = () => {
  const topLayer = "2147483647";
  const nearTopLayer = "2147483646";
  const overrideStyleId = "elite-privacy-banner-overrides";
  const consentChoiceKey = "elitepcs_privacy_consent_choice";
  const overrideCss = `
    html.elite-privacy-banner-dismissed #shopify-pc__banner,
    html.elite-privacy-banner-dismissed #shopify-pc__banner.shopify-pc__banner__dialog,
    html.elite-privacy-banner-dismissed .shopify-pc__banner__dialog {
      display: none !important;
      pointer-events: none !important;
    }

    #shopify-pc__banner,
    #shopify-pc__banner.shopify-pc__banner__dialog,
    .shopify-pc__banner__dialog {
      position: fixed !important;
      inset: auto auto 18px 50% !important;
      transform: translateX(-50%) !important;
      z-index: 2147483647 !important;
      isolation: isolate !important;
      pointer-events: auto !important;
      box-sizing: border-box !important;
      width: min(640px, calc(100vw - 36px)) !important;
      max-width: calc(100vw - 36px) !important;
      max-height: calc(100vh - 36px) !important;
      overflow: auto !important;
      padding: 18px 20px !important;
      background: #ffffff !important;
      color: #08090b !important;
      border: 1px solid #000000 !important;
      box-shadow: 0 24px 56px rgba(0, 0, 0, 0.28) !important;
      border-radius: 0 !important;
    }

    #shopify-pc__banner .shopify-pc__banner__wrapper,
    .shopify-pc__banner__dialog .shopify-pc__banner__wrapper {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      align-items: start !important;
      gap: 12px !important;
      pointer-events: auto !important;
    }

    #shopify-pc__banner .shopify-pc__banner__body,
    .shopify-pc__banner__dialog .shopify-pc__banner__body {
      display: grid !important;
      gap: 6px !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    #shopify-pc__banner *,
    .shopify-pc__banner__dialog * {
      box-sizing: border-box !important;
      max-width: 100% !important;
      pointer-events: auto !important;
      color: #08090b !important;
      letter-spacing: 0 !important;
    }

    #shopify-pc__banner h2,
    .shopify-pc__banner__dialog h2 {
      margin: 0 !important;
      padding: 0 !important;
      font-size: 1rem !important;
      line-height: 1.2 !important;
    }

    #shopify-pc__banner p,
    .shopify-pc__banner__dialog p {
      margin: 0 !important;
      padding: 0 !important;
      font-size: 0.86rem !important;
      line-height: 1.45 !important;
    }

    #shopify-pc__banner .shopify-pc__banner__btns,
    .shopify-pc__banner__dialog .shopify-pc__banner__btns {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 8px 10px !important;
      width: 100% !important;
      justify-content: flex-end !important;
      margin: 2px 0 0 !important;
      padding: 0 !important;
    }

    #shopify-pc__banner button,
    #shopify-pc__banner a,
    .shopify-pc__banner__dialog button,
    .shopify-pc__banner__dialog a {
      position: relative !important;
      z-index: 2147483647 !important;
      pointer-events: auto !important;
      cursor: pointer !important;
      touch-action: manipulation !important;
      margin: 0 !important;
    }

    #shopify-pc__banner button,
    .shopify-pc__banner__dialog button {
      min-height: 42px !important;
      padding: 10px 18px !important;
      flex: 0 0 auto !important;
      flex-basis: auto !important;
      width: auto !important;
      min-width: 92px !important;
      background: #ffffff !important;
      border: 1px solid #000000 !important;
      color: #08090b !important;
      border-radius: 0 !important;
      font-weight: 900 !important;
      line-height: 1.15 !important;
      white-space: nowrap !important;
    }

    #shopify-pc__banner .shopify-pc__banner__btn-manage-prefs,
    .shopify-pc__banner__dialog .shopify-pc__banner__btn-manage-prefs {
      margin-right: auto !important;
    }

    #shopify-pc__banner .shopify-pc__banner__btn-accept,
    .shopify-pc__banner__dialog .shopify-pc__banner__btn-accept {
      background: #000000 !important;
      border-color: #000000 !important;
      color: #ffffff !important;
    }

    #shopify-pc__banner::before,
    #shopify-pc__banner::after,
    .shopify-pc__banner__dialog::before,
    .shopify-pc__banner__dialog::after {
      pointer-events: none !important;
    }

    #shopify-pc__prefs,
    .shopify-pc__prefs {
      position: fixed !important;
      inset: 0 !important;
      z-index: 2147483646 !important;
    }

    #shopify-pc__prefs[hidden],
    .shopify-pc__prefs[hidden],
    #shopify-pc__prefs[aria-hidden="true"],
    .shopify-pc__prefs[aria-hidden="true"] {
      pointer-events: none !important;
    }

    .shopify-pc__prefs__overlay {
      z-index: 2147483646 !important;
      pointer-events: auto !important;
    }

    .shopify-pc__prefs__dialog {
      position: fixed !important;
      inset: 50% auto auto 50% !important;
      transform: translate(-50%, -50%) !important;
      z-index: 2147483647 !important;
      pointer-events: auto !important;
      background: #ffffff !important;
      border: 1px solid #000000 !important;
      color: #08090b !important;
      border-radius: 0 !important;
    }

    @media (max-width: 760px) {
      #shopify-pc__banner,
      #shopify-pc__banner.shopify-pc__banner__dialog,
      .shopify-pc__banner__dialog {
        inset: auto 12px 12px 12px !important;
        width: auto !important;
        max-width: none !important;
        transform: none !important;
        padding: 14px !important;
      }

      #shopify-pc__banner .shopify-pc__banner__btns,
      .shopify-pc__banner__dialog .shopify-pc__banner__btns {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
      }

      #shopify-pc__banner button,
      .shopify-pc__banner__dialog button {
        width: 100% !important;
        min-width: 0 !important;
        padding-inline: 12px !important;
      }

      #shopify-pc__banner .shopify-pc__banner__btn-manage-prefs,
      .shopify-pc__banner__dialog .shopify-pc__banner__btn-manage-prefs {
        grid-column: 1 / -1 !important;
        margin-right: 0 !important;
      }
    }
  `;
  const selectors = [
    "#shopify-pc__banner",
    ".shopify-pc__banner",
    ".shopify-pc__banner__dialog",
    "#shopify-pc__prefs",
    ".shopify-pc__prefs",
    "#shopify-pc__prefs__overlay",
    ".shopify-pc__prefs__overlay",
    ".shopify-pc__prefs__dialog"
  ].join(",");

  const setImportant = (node, property, value) => {
    node.style.setProperty(property, value, "important");
  };

  const storedConsentChoice = () => {
    try {
      return window.localStorage.getItem(consentChoiceKey);
    } catch {
      return "";
    }
  };

  const rememberConsentChoice = (choice) => {
    try {
      window.localStorage.setItem(consentChoiceKey, JSON.stringify({
        choice,
        at: new Date().toISOString()
      }));
    } catch {
      // Ignore blocked storage. Shopify's own consent API is still attempted.
    }
  };

  const markBannerDismissed = () => {
    document.documentElement.classList.add("elite-privacy-banner-dismissed");

    document.querySelectorAll("#shopify-pc__banner, .shopify-pc__banner__dialog").forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      node.hidden = true;
      node.setAttribute("aria-hidden", "true");
      setImportant(node, "display", "none");
      setImportant(node, "pointer-events", "none");
    });
  };

  const consentButtonAction = (target) => {
    if (!(target instanceof Element)) return "";

    const button = target.closest(
      "#shopify-pc__banner button, .shopify-pc__banner__dialog button"
    );

    if (!(button instanceof HTMLElement)) return "";

    const token = [
      button.id,
      button.className,
      button.textContent
    ].join(" ").toLowerCase();

    if (token.includes("accept")) return "accept";
    if (token.includes("decline")) return "decline";

    return "";
  };

  const registerConsentChoice = (choice, callback) => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    const didAccept = choice === "accept";
    let completed = false;

    const finish = () => {
      if (completed) return;
      completed = true;
      callback();
    };

    if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== "function") {
      finish();
      return;
    }

    try {
      customerPrivacy.setTrackingConsent({
        analytics: didAccept,
        marketing: didAccept,
        preferences: didAccept
      }, finish);
      window.setTimeout(finish, 500);
    } catch {
      finish();
    }
  };

  const ensureOverrideStyle = () => {
    const host = document.head || document.documentElement;
    if (!host) return;

    let style = document.getElementById(overrideStyleId);
    if (!style) {
      style = document.createElement("style");
      style.id = overrideStyleId;
      style.type = "text/css";
    }

    if (style.textContent !== overrideCss) {
      style.textContent = overrideCss;
    }

    if (style.parentNode !== host || host.lastElementChild !== style) {
      host.append(style);
    }
  };

  const liftNode = (node) => {
    if (!(node instanceof HTMLElement)) return;

    setImportant(node, "pointer-events", "auto");

    if (node.matches("#shopify-pc__banner, .shopify-pc__banner, .shopify-pc__banner__dialog")) {
      setImportant(node, "position", "fixed");
      setImportant(node, "bottom", "18px");
      setImportant(node, "left", "50%");
      setImportant(node, "right", "auto");
      setImportant(node, "top", "auto");
      setImportant(node, "transform", "translateX(-50%)");
      setImportant(node, "z-index", topLayer);
      setImportant(node, "isolation", "isolate");
      setImportant(node, "pointer-events", "auto");
      setImportant(node, "background", "#ffffff");
      setImportant(node, "border", "1px solid #000000");
      setImportant(node, "color", "#08090b");
      setImportant(node, "box-shadow", "0 24px 56px rgba(0, 0, 0, 0.28)");
      setImportant(node, "border-radius", "0");
      setImportant(node, "width", "min(640px, calc(100vw - 36px))");
      setImportant(node, "max-width", "calc(100vw - 36px)");
      setImportant(node, "max-height", "calc(100vh - 36px)");
      setImportant(node, "overflow", "auto");
    }

    if (node.matches("#shopify-pc__prefs, .shopify-pc__prefs")) {
      setImportant(node, "position", "fixed");
      setImportant(node, "inset", "0");
      setImportant(node, "z-index", nearTopLayer);
      setImportant(node, "pointer-events", node.hidden || node.getAttribute("aria-hidden") === "true" ? "none" : "auto");
    }

    if (node.matches("#shopify-pc__prefs__overlay, .shopify-pc__prefs__overlay")) {
      setImportant(node, "z-index", nearTopLayer);
      setImportant(node, "pointer-events", "auto");
    }

    if (node.matches(".shopify-pc__prefs__dialog")) {
      setImportant(node, "position", "fixed");
      setImportant(node, "z-index", topLayer);
      setImportant(node, "pointer-events", "auto");
    }

    node.querySelectorAll("button, a").forEach((control) => {
      if (!(control instanceof HTMLElement)) return;
      setImportant(control, "position", "relative");
      setImportant(control, "z-index", topLayer);
      setImportant(control, "pointer-events", "auto");
      setImportant(control, "cursor", "pointer");
    });
  };

  const liftPrivacyBanner = () => {
    ensureOverrideStyle();
    if (storedConsentChoice()) {
      markBannerDismissed();
      return;
    }
    document.querySelectorAll(selectors).forEach(liftNode);
  };

  document.addEventListener("click", (event) => {
    const action = consentButtonAction(event.target);
    if (!action) return;

    window.setTimeout(() => {
      registerConsentChoice(action, () => {
        rememberConsentChoice(action);
        markBannerDismissed();
      });
    }, 80);
  }, true);

  liftPrivacyBanner();

  let privacyUpdateQueued = false;
  new MutationObserver((mutations) => {
    // Configurator and cart updates do not change the privacy banner. Only
    // reapply its presentation when its own DOM or a competing head style changes.
    const relevant = mutations.some((mutation) => {
      const target = mutation.target;
      if (target instanceof Element && target.closest(selectors)) return true;
      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element) || node.id === overrideStyleId) return false;
        if (node.matches(selectors) || node.querySelector(selectors)) return true;
        return target === document.head && node.matches('style, link[rel="stylesheet"]');
      });
    });
    if (!relevant || privacyUpdateQueued) return;
    privacyUpdateQueued = true;
    window.requestAnimationFrame(() => {
      privacyUpdateQueued = false;
      liftPrivacyBanner();
    });
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.setTimeout(liftPrivacyBanner, 500);
  window.setTimeout(liftPrivacyBanner, 1500);
  window.setTimeout(liftPrivacyBanner, 3000);
  window.addEventListener("load", liftPrivacyBanner);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initElitePrivacyBannerLayer);
} else {
  initElitePrivacyBannerLayer();
}
